// ============================================================
// TMR Portal Admin — a failed write, said out loud
// ============================================================
import { ApiError } from '@tmr/api-client';
import { Icons, StrokeIcon } from '../../lib/icons';

/** Props for {@link ErrorBanner}. */
interface ErrorBannerProps {
  /** The failure to explain; null renders nothing. */
  error: unknown;
  /** Overrides the text for a specific `code` — e.g. a 409 the form can phrase better than the server. */
  detailFor?: (error: ApiError) => string | null;
}

/**
 * Shows why a write failed, inline and next to the control that failed.
 *
 * Authorization writes fail in ways the operator can act on — the role name is taken, the role grants
 * more than the operator holds, Clerk is unreachable — and each of those is a different next step. The
 * server's `detail` is already operator-facing PT-BR, so it is shown rather than replaced; a 502 is the
 * one case worth rephrasing, since "tente de novo" is the whole instruction (06 §6.1).
 */
export function ErrorBanner({ error, detailFor }: ErrorBannerProps) {
  if (!error) return null;

  const apiError = error instanceof ApiError ? error : null;
  const override = apiError && detailFor ? detailFor(apiError) : null;
  const message =
    override ??
    (apiError?.status === 502
      ? 'Não foi possível concluir agora. Nada foi gravado — tente novamente em instantes.'
      : (apiError?.detail ?? 'Não foi possível concluir a operação. Tente novamente.'));

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '11px 13px',
        border: '1px solid var(--danger)',
        borderRadius: 'var(--radius-sm)',
        background: 'oklch(97% 0.02 28)',
        fontSize: 13,
        color: 'var(--ink-900)',
        lineHeight: 1.5,
      }}
    >
      <StrokeIcon d={Icons.alert} size={16} style={{ flexShrink: 0, color: 'var(--danger)' }} />
      <span>{message}</span>
    </div>
  );
}
